import {
  coerceDateTime,
  coerceLocalDate,
  destinationLabel,
  encodeDestinationSummary,
  lifecycleStatusLabel,
  memberRoleLabel,
  parseDestinationSummary,
} from '../my-journeys/myJourneysModel';
import { normalizeCoordinatePair } from '../explore/socialMapCoordinate';
import {
  buildJourneySpatialMarkers,
  toPlanningCompatibleMapMarker,
} from '../journey-spatial/journeySpatialMarkerModel';
import {
  ACTIVITY_TYPE_OPTIONS,
  formatPlanningDayHeading,
  planningConflictUserMessage,
} from './planningPresentation';

const DAY_EDITOR_ROLES = new Set(['OWNER', 'COORDINATOR']);
const ACTIVITY_WRITER_ROLES = new Set(['OWNER', 'COORDINATOR', 'MEMBER']);
const ACTIVITY_MANAGER_ROLES = new Set(['OWNER', 'COORDINATOR']);
const WRITABLE_LIFECYCLES = new Set(['DRAFT', 'READY', 'TRAVELING']);
export const CHAT_SHARE_LIFECYCLES = new Set(['READY', 'TRAVELING', 'COMPLETED']);

/** Product-facing type options (zh labels); values remain API enums. */
export const ACTIVITY_TYPES = ACTIVITY_TYPE_OPTIONS;

const pad2 = (value) => String(value).padStart(2, '0');

/** Coerce LocalTime ISO string or Jackson numeric array into HH:mm for time inputs. */
export function coerceLocalTime(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : value;
  }
  if (Array.isArray(value) && value.length >= 2) {
    return `${pad2(value[0])}:${pad2(value[1])}`;
  }
  return '';
}

/** Normalize HH:mm (or HH:mm:ss) for Travel LocalTime JSON. */
export function toApiLocalTime(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(text)) return text;
  if (/^\d{2}:\d{2}$/.test(text)) return `${text}:00`;
  return text;
}

export function encodeLocationSummary(text) {
  return encodeDestinationSummary(text);
}

export function locationLabelFromSummary(raw) {
  return destinationLabel(raw);
}

export function normalizeActivityLocation(value) {
  if (!value || typeof value !== 'object') return null;
  const id = snowflakeId(value.id ?? value.locationId);
  if (!id) return null;
  const name = String(value.name || value.displayName || '').trim();
  const address = value.address == null || String(value.address).trim() === ''
    ? null
    : String(value.address).trim();
  return {
    id,
    name: name || 'Location',
    address,
    latitude: value.latitude ?? null,
    longitude: value.longitude ?? null,
  };
}

export function activityLocationDisplay(activity) {
  if (activity?.location?.name) {
    return {
      name: activity.location.name,
      address: activity.location.address || '',
      unresolved: false,
    };
  }
  if (activity?.locationId) {
    return {
      name: activity.locationLabel || 'Location unavailable',
      address: '',
      unresolved: true,
    };
  }
  if (activity?.locationLabel) {
    return {
      name: activity.locationLabel,
      address: '',
      unresolved: false,
    };
  }
  return null;
}

/**
 * Planning map markers — AUTH59：委派 shared spatial model，再適配既有 renderer shape。
 * 產品行為（eligibility／dayLabel／unscheduled）須與抽離前一致。
 */
export function buildPlanningMapMarkers(schedule) {
  return buildJourneySpatialMarkers(schedule, {
    formatDayLabel: dayLabel,
  }).map(toPlanningCompatibleMapMarker);
}

/**
 * UX-03-B: unsaved place selection preview marker (frontend-only).
 * - coordinates authority: only from canonical Travel geo node resolved read model
 *   (draft location node keeps latitude/longitude)
 * - API contract unchanged: does NOT serialize coords to Activity payload
 */
export function buildDraftPreviewMapMarker({
  previewKey,
  draftTitle,
  dayLabel,
  locationNode,
}) {
  if (!previewKey || !locationNode) return null;
  const coords = normalizeCoordinatePair({
    latitude: locationNode.latitude,
    longitude: locationNode.longitude,
  });
  if (!coords.markerAvailable) return null;

  const name = String(locationNode.displayName || locationNode.name || '').trim();
  const address = locationNode.address == null ? '' : String(locationNode.address).trim();
  const locationName = name ? `${name}（預覽）` : 'Location（預覽）';

  return {
    key: previewKey,
    // Keep shape compatible with persisted markers; activityId is not used by renderer.
    activityId: null,
    title: (draftTitle || '').trim() || '未儲存安排',
    dayLabel: `${dayLabel}（未儲存預覽）`,
    locationName,
    address,
    summary: address || name || locationName,
    latitude: coords.latitude,
    longitude: coords.longitude,
  };
}

function dayLabel(day) {
  return formatPlanningDayHeading(day);
}

export const PLANNING_MAP_RENDERER = 'GOOGLE_MAPS_JS';
export const PLANNING_MAP_LOCATION_AUTHORITY = 'GEO_LOCATION_NODE';

export function snowflakeId(value) {
  if (value == null || value === '') return null;
  return String(value);
}

export function classifyPlanningError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 92001 || code === 93001 || key.includes('not.found')) return 'not-found';
  if (code === 92002 || code === 93002 || key.includes('permission.denied')) return 'forbidden';
  if (code === 92003 || code === 93003
      || key.includes('concurrent.modification') || key.includes('conflict')
      || key.includes('lifecycle.not.writable') || key.includes('day.has.activities')
      || key.includes('day.duplicate.date') || key.includes('day.out.of.range')) {
    return 'conflict';
  }
  if (code === 92000 || code === 93000 || key.includes('validation')) return 'validation';
  if (code === 93004 || key.includes('im.unavailable')) return 'unavailable';
  return 'error';
}

export function planningConflictMessage(cause) {
  return planningConflictUserMessage(cause);
}

export function normalizeActivitySummary(value) {
  const location = normalizeActivityLocation(value?.location);
  const coords = normalizeCoordinatePair(location);
  return {
    activityId: snowflakeId(value?.activityId),
    journeyDayId: snowflakeId(value?.journeyDayId),
    title: value?.title || 'Untitled activity',
    activityType: value?.activityType || 'CUSTOM',
    plannedStartTime: coerceLocalTime(value?.plannedStartTime),
    plannedEndTime: coerceLocalTime(value?.plannedEndTime),
    displayOrder: Number(value?.displayOrder) || 0,
    status: value?.status || 'PLANNED',
    locationId: snowflakeId(value?.locationId),
    location,
    locationSummaryJson: value?.locationSummaryJson || null,
    locationLabel: location?.name || locationLabelFromSummary(value?.locationSummaryJson),
    hasCoordinates: coords.markerAvailable,
    createdByMemberId: snowflakeId(value?.createdByMemberId),
    updateTime: coerceDateTime(value?.updateTime),
    description: value?.description || '',
  };
}

export function normalizeActivityDetail(value) {
  return {
    ...normalizeActivitySummary(value),
    plannedDate: coerceLocalDate(value?.plannedDate),
    description: value?.description || '',
    itineraryRowVersion: value?.itineraryRowVersion ?? null,
  };
}

export function normalizeScheduleDay(value) {
  const activities = Array.isArray(value?.activities)
    ? value.activities.map(normalizeActivitySummary).sort((a, b) => a.displayOrder - b.displayOrder)
    : [];
  return {
    journeyDayId: snowflakeId(value?.journeyDayId),
    displayOrder: Number(value?.displayOrder) || 0,
    date: coerceLocalDate(value?.date),
    title: value?.title || '',
    activities,
    activityCount: activities.length,
  };
}

export function normalizePlanningSchedule(value) {
  const days = Array.isArray(value?.days)
    ? value.days.map(normalizeScheduleDay).sort((a, b) => a.displayOrder - b.displayOrder)
    : [];
  const unscheduled = Array.isArray(value?.unscheduledActivities)
    ? value.unscheduledActivities.map(normalizeActivitySummary)
    : [];
  return {
    journeyId: snowflakeId(value?.journeyId),
    journeyLifecycleStatus: value?.journeyLifecycleStatus || '',
    itineraryId: snowflakeId(value?.itineraryId),
    itineraryRowVersion: value?.itineraryRowVersion ?? null,
    days,
    unscheduledActivities: unscheduled,
  };
}

export function planningCapabilities(journey, schedule) {
  const role = journey?.requesterRole || '';
  const lifecycle = schedule?.journeyLifecycleStatus || journey?.lifecycleStatus || '';
  const writable = WRITABLE_LIFECYCLES.has(lifecycle);
  return {
    canManageDays: writable && DAY_EDITOR_ROLES.has(role),
    canCreateActivity: writable && ACTIVITY_WRITER_ROLES.has(role),
    canMoveActivity: writable && ACTIVITY_WRITER_ROLES.has(role),
    canRemoveActivity: writable && ACTIVITY_MANAGER_ROLES.has(role),
    canEditActivity: writable && ACTIVITY_WRITER_ROLES.has(role),
    canShareToChat: CHAT_SHARE_LIFECYCLES.has(lifecycle) && ACTIVITY_WRITER_ROLES.has(role),
    lifecycleLabel: lifecycleStatusLabel(lifecycle),
    roleLabel: memberRoleLabel(role),
  };
}

/**
 * Normalize a time-field input before XOR / order checks.
 * - null/undefined → null
 * - whitespace-only / "" → null
 * - otherwise trimmed string (HH:mm kept as-is)
 */
export function normalizeActivityTimeInput(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
}

/**
 * Validate activity time pair (PD-TIME-01 / PD-TIME-02).
 * - Both null  → valid   (無時間事件)
 * - Both set   → valid if end >= start
 * - XOR        → invalid (Chinese message)
 */
export function validateActivityTimes(startTime, endTime) {
  const start = normalizeActivityTimeInput(startTime);
  const end = normalizeActivityTimeInput(endTime);
  const hasStart = Boolean(start);
  const hasEnd = Boolean(end);
  // XOR: one set, other not → invalid
  if (hasStart !== hasEnd) {
    return '請同時填寫開始與結束時間，或兩者都不填。';
  }
  // Both null → valid (無時間事件)
  if (!hasStart) return '';
  // Both set → check temporal order (same-day string compare; no cross-midnight expansion)
  if (toApiLocalTime(end) < toApiLocalTime(start)) {
    return '結束時間不可早於開始時間。';
  }
  return '';
}

/** True when both sides form a valid same-day timed pair (used by smart-next). */
export function isValidTimedActivityPair(startTime, endTime) {
  const start = normalizeActivityTimeInput(startTime);
  const end = normalizeActivityTimeInput(endTime);
  if (!start || !end) return false;
  const startApi = toApiLocalTime(start);
  const endApi = toApiLocalTime(end);
  if (!/^\d{2}:\d{2}/.test(startApi) || !/^\d{2}:\d{2}/.test(endApi)) return false;
  return endApi >= startApi;
}

/**
 * Default end = start + 60 minutes (HH:mm arithmetic; no Date / locale parsing).
 * Clamps to 23:59 same day (does not invent cross-midnight contract).
 */
export function suggestDefaultEndTime(startHhMm) {
  const start = normalizeActivityTimeInput(startHhMm) || '08:00';
  const match = start.match(/^(\d{2}):(\d{2})/);
  if (!match) return '09:00';
  let hours = Number(match[1]);
  let minutes = Number(match[2]) + 60;
  hours += Math.floor(minutes / 60);
  minutes %= 60;
  if (hours >= 24) return '23:59';
  return `${pad2(hours)}:${pad2(minutes)}`;
}

/**
 * Suggest start time for the next activity being added to a day (PD-TIME-02).
 * - Empty day or no timed events → 08:00
 * - Otherwise → last valid endTime in displayOrder
 * - No-time / XOR / invalid events are skipped
 * - Cross-day reset: caller passes only the target day's activities
 */
export function suggestNextActivityStartTime(dayActivities, { excludeActivityId } = {}) {
  const exclude = excludeActivityId == null ? null : String(excludeActivityId);
  const list = Array.isArray(dayActivities)
    ? (exclude
      ? dayActivities.filter((act) => String(act?.activityId) !== exclude)
      : dayActivities)
    : [];
  if (list.length === 0) return '08:00';
  const sorted = [...list].sort((a, b) => a.displayOrder - b.displayOrder);
  let lastEnd = null;
  for (const act of sorted) {
    if (!isValidTimedActivityPair(act.plannedStartTime, act.plannedEndTime)) continue;
    lastEnd = coerceLocalTime(normalizeActivityTimeInput(act.plannedEndTime));
  }
  return lastEnd || '08:00';
}

/**
 * 是否存在可作為 smart-next 錨點的前一筆 timed Activity。
 * 首筆僅套用預設 08:00 時不得顯示「已依前一筆安排建議」。
 */
export function hasPriorTimedActivity(dayActivities, { excludeActivityId } = {}) {
  const exclude = excludeActivityId == null ? null : String(excludeActivityId);
  const list = Array.isArray(dayActivities)
    ? (exclude
      ? dayActivities.filter((act) => String(act?.activityId) !== exclude)
      : dayActivities)
    : [];
  return list.some((act) => isValidTimedActivityPair(act.plannedStartTime, act.plannedEndTime));
}

/**
 * Timed defaults for a new activity draft (init / no-time→timed only).
 * Pass excludeActivityId when editing so the activity itself is not used as the smart-next anchor.
 */
export function buildTimedActivityDefaults(dayActivities, { excludeActivityId } = {}) {
  const plannedStartTime = suggestNextActivityStartTime(dayActivities, { excludeActivityId });
  return {
    plannedStartTime,
    plannedEndTime: suggestDefaultEndTime(plannedStartTime),
    timeMode: 'timed',
    fromPriorActivity: hasPriorTimedActivity(dayActivities, { excludeActivityId }),
  };
}

export { parseDestinationSummary, encodeDestinationSummary };
